'use client';

import * as React from 'react';
import { CheckIcon, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import {
	Command,
	CommandGroup,
	CommandItem,
	CommandList,
} from '@/components/ui/command';
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Typography, Icon } from '@/components/common';
import Icons from '@/const/icons';
import { InputText } from '../form';
import { useForm } from 'react-hook-form';

export type Options = {
	icon?: JSX.Element | string;
	label?: string | React.ReactNode;
	value?: string;
};

interface ComboboxMultipleSelectionProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	disabled?: boolean;
	hasFilter: boolean;
	buttonContentDefault?: boolean;
	values?: string[];
	labelDropdown?: string;
	searchTitle?: string;
	classNameTitle?: string;
	classNamePopoverContent?: string;
	classNameClear?: string;
	classNameTrigger?: string;
	filterOptions: Options[];
	withSearch?: boolean;
	handleClearAll?: () => void;
	handleSelectFilters?: (value: string | undefined) => void;
	handleSearch?: (value: string) => void;
}

export const ButtonContentFilter = ({
	active,
	classNameTrigger,
}: {
	active: boolean;
	classNameTrigger?: string;
}) => {
	return (
		<div className={clsx('relative w-fit cursor-pointer', classNameTrigger)}>
			<Icon
				src={Icons.filter}
				width={16}
				height={16}
				className="text-opacity-black"
			/>
			{active && (
				<span
					className={clsx(
						'bg-main-blue rounded-full w-[10px] h-[10px] absolute -right-2 -top-[0.1rem] border border-white'
					)}
				/>
			)}
		</div>
	);
};

export const ButtonDropdownFilter = ({
	open,
	label,
	value,
	classNameTrigger,
}: {
	open: boolean;
	label?: string;
	value?: string;
	classNameTrigger?: string;
}) => {
	return (
		<Button
			variant="outline"
			aria-expanded={open}
			className={clsx('relative gap-2 min-w-max bg-white', classNameTrigger)}
		>
			<Typography
				type="semibold-14"
				className={clsx(
					'!line-clamp-1 flex-1 text-ellipsis overflow-hidden whitespace-normal text-left opacity-60'
				)}
			>
				{label}
			</Typography>
			<ChevronDown
				className={clsx('mt-0.5 h-5 w-5 shrink-0 text-[#0D0616]', {
					'rotate-180': open,
				})}
			/>
			{value && (
				<span className="w-[10px] h-[10px] bg-main-blue rounded-full absolute -top-1 -right-1 z-20" />
			)}
		</Button>
	);
};

export const ComboboxMultipleSelection: React.FC<
	ComboboxMultipleSelectionProps
> = ({
	open,
	setOpen,
	disabled,
	hasFilter,
	buttonContentDefault,
	values,
	labelDropdown,
	searchTitle,
	classNameTitle,
	classNamePopoverContent,
	classNameTrigger,
	classNameClear,
	filterOptions,
	handleClearAll,
	handleSelectFilters,
	handleSearch,
	withSearch = false,
}) => {
	const { register } = useForm();

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger
				asChild={buttonContentDefault}
				disabled={disabled}
				className="disabled:cursor-not-allowed"
			>
				{!buttonContentDefault ? (
					<ButtonContentFilter
						active={hasFilter}
						classNameTrigger={classNameTrigger}
					/>
				) : (
					<div>
						<ButtonDropdownFilter
							open={open}
							label={labelDropdown}
							value={values?.[0]}
							classNameTrigger={classNameTrigger}
						/>
					</div>
				)}
			</PopoverTrigger>
			<PopoverContent
				className={clsx(
					'w-[300px] max-h-96 p-0 border-none z-10 overflow-y-auto scroll-custom shadow-shadow2',
					classNamePopoverContent
				)}
				align="start"
				side="bottom"
			>
				<Command>
					{searchTitle && (
						<Typography
							type="semibold-16"
							className={clsx('mb-2 px-4 pt-3', classNameTitle)}
						>
							{searchTitle}
						</Typography>
					)}
					<CommandList>
						<CommandGroup className="p-0">
							{handleClearAll && (
								<Button
									variant={'ghost'}
									className={clsx(
										'font-normal flex justify-start items-center gap-2 h-12 px-4 w-full hover:bg-grey-100 rounded-none',
										classNameClear
									)}
									onClick={() => {
										handleClearAll();
										setOpen(false);
									}}
								>
									<Icon src={Icons.broom} className="!size-5 text-primary" />
									<Typography type="regular-14">Clear All</Typography>
								</Button>
							)}
							{withSearch && (
								<div className="bg-white text-black pt-2 pb-4 px-4">
									<InputText
										autoFocus
										name="search"
										register={register}
										placeholder="Search"
										iconLeft={Icons.search}
										className="w-full"
										classNameIcon={'!top-[23%] !w-4 !h-4 text-opacity-black'}
										classNameInput="pl-9 !bg-gray-15 !h-[33px] !m-0 border-none 3xl:!text-sm"
										onChange={({ target }) => {
											handleSearch?.(target.value);
										}}
									/>
								</div>
							)}
							{filterOptions.map((option, index) => (
								<CommandItem
									key={`${option.value}-${index}`}
									onSelect={() => {
										handleSelectFilters && handleSelectFilters(option.value);
									}}
									className="mb-3 px-4 py-2 cursor-pointer hover:bg-grey-100"
								>
									<div
										className="flex items-center gap-2 min-w-0 w-full"
										title={
											typeof option.label === 'string'
												? option.label
												: undefined
										}
									>
										<div className="flex items-center justify-center w-6 h-6 shrink-0 rounded border border-grey-300">
											{values?.includes(option.value as string) && (
												<CheckIcon
													className="w-5 h-5 text-green-60"
													aria-hidden="true"
												/>
											)}
										</div>
										{option.icon}
										<Typography type="regular-14" className="truncate min-w-0">
											{option.label}
										</Typography>
									</div>
								</CommandItem>
							))}
						</CommandGroup>
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
};
